import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, output, input, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';

import { ApButtonLinkComponent, ApButtonComponent } from '@ap/button';
import { ApWindowService } from '@ap/cdk';
import { ApContentService, ApContentPipe, ApContentComponent } from '@ap/content';
import { ApIconComponent } from '@ap/icon';
import { ApIllustrationComponent } from '@ap/illustration';
import { ErrorCode, SessionBindingInfo } from '@ap/interface-apic-authenticatie-fido';
import { TaskStatus } from '@ap/interface-apic-authenticatiebeheer';
import { ApNotificationComponent } from '@ap/notification';
import { QrCodeOptions, ApQrCodeComponent } from '@ap/qr-code';

import { SignError } from '../../sign.model';
import { ApIcmpErrorService } from '../../icmp/icmp.service';

import { ApNativeAppInstructionsComponent } from './instructions/instructions.component';
import { ApNativeAppSkeletonComponent } from './skeleton/skeleton.component';
import content from './native-app.content.json';
import { ApNativeAppService } from './native-app.service';

/**
 * @ngdoc component
 * @name ApNativeAppComponent
 */
@Component({
    selector: 'ap-native-app',
    standalone: true,
    templateUrl: 'native-app.component.html',
    styleUrls: ['./native-app.component.scss'],
    providers: [ApNativeAppService],
    imports: [
        ApNativeAppSkeletonComponent,
        ApNotificationComponent,
        ApContentComponent,
        ApButtonLinkComponent,
        ApQrCodeComponent,
        ApNativeAppInstructionsComponent,
        ApIllustrationComponent,
        ApIconComponent,
        ApButtonComponent,
        ApContentPipe
    ]
})
export class ApNativeAppComponent implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly icmpErrorService = inject(ApIcmpErrorService);
    private readonly nativeAppService = inject(ApNativeAppService);
    private readonly windowService = inject(ApWindowService);
    private readonly domSanitizer = inject(DomSanitizer);

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Required - Initial status of sign
     */
    public readonly initialStatus = input<TaskStatus>(undefined);

    /**
     * Emits event when user cancels sign process
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when signing fails
     */
    public signError = output<SignError>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<void>();

    public qrContent: string;
    public qrPickedUp: boolean;
    public qrTimedOut: boolean;
    public qrOptions: QrCodeOptions;

    public showMobileError: boolean;

    private readonly CHALLENGE_REFRESH_TIME = 5 * 1000; // 5 seconds
    private readonly SIGN_STATUS_REFRESH_TIME = 1000; // 1 second

    private readonly subscriptions: Subscription = new Subscription();

    public get mobileAppHref() {
        const url = new URL(this.qrContent);
        url.searchParams.set('qr', 'false');
        return this.domSanitizer.bypassSecurityTrustUrl(url.toString());
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.icmpErrorService
            .hasError()
            .pipe(filter((error: SignError) => error !== undefined))
            .subscribe((error: SignError) => this.signError.emit(error));
        this.qrOptions = this.getQrOptions();

        const actionId = this.actionId();
        this.subscriptions.add(this.pollToRefreshChallenge(actionId));
        this.subscriptions.add(this.pollToGetSignStatus(actionId));
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public emitRetry(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.retry.emit();
    }

    public emitCancel(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.canceled.emit();
    }

    public isMobileBrowser() {
        return this.windowService.supportsNativeApp();
    }

    public canScan(): boolean {
        return this.qrContent && !this.qrTimedOut;
    }

    public isPickedUpByNativeApp(): boolean {
        return this.qrPickedUp && !this.qrTimedOut;
    }

    public hasTimedOut(): boolean {
        return this.qrTimedOut;
    }

    public isLoaded(): boolean {
        return !!this.qrContent || this.qrPickedUp || this.qrTimedOut;
    }

    public mobileAppButtonClicked(): void {
        this.showMobileError = true;
    }

    public rescan(): void {
        this.nativeAppService.unbindChallenge(this.actionId()).subscribe();
    }

    private getQrContent(actionId: string, challenge: string): string {
        return challenge
            ? this.contentService.get('native-app.qr.code.content', {
                  actionid: actionId,
                  challenge
              })
            : undefined;
    }

    private getQrOptions(): QrCodeOptions {
        return { errorCorrectionLevel: 'L', width: 205 };
    }

    private pollToRefreshChallenge(actionId: string): Subscription {
        return this.nativeAppService
            .pollChallenge(actionId, this.CHALLENGE_REFRESH_TIME)
            .pipe(takeUntil(outputToObservable(this.done)))
            .subscribe((challenge: SessionBindingInfo | HttpErrorResponse) => {
                if (challenge instanceof HttpErrorResponse) {
                    this.signError.emit({ key: challenge.error.code, type: 'error' });
                    // TODO: The 'emit' function requires a mandatory void argument
                    this.done.emit();
                    delete this.qrContent;
                } else {
                    this.qrPickedUp = !!challenge && challenge.is_bound;
                    if (this.qrPickedUp || !challenge) {
                        delete this.qrContent;
                    } else if (challenge) {
                        this.qrContent = this.getQrContent(actionId, challenge.challenge);
                    }
                    if (challenge) {
                        // emit to reset any previous errors from retrieving the challenge
                        this.signError.emit(undefined);
                    }
                }
            });
    }

    private pollToGetSignStatus(actionId: string): Subscription {
        return this.nativeAppService
            .pollAuthenticationStatus(actionId, this.SIGN_STATUS_REFRESH_TIME)
            .pipe(
                catchError((error) => this.handleSignStatusError(error)),
                tap((status: TaskStatus) => {
                    if (this.isExpired(status)) {
                        this.qrTimedOut = true;
                    }
                }),
                tap((signStatus: TaskStatus) => {
                    if (signStatus !== undefined && this.isReady(signStatus) && !this.qrTimedOut) {
                        // TODO: The 'emit' function requires a mandatory void argument
                        this.done.emit();
                    }
                }),
                takeWhile((signStatus: TaskStatus) => signStatus === undefined || !this.isReady(signStatus))
            )
            .subscribe((actionStatus: TaskStatus) => {
                if (this.qrContent !== undefined && actionStatus !== undefined) {
                    this.signError.emit(undefined);
                }
            });
    }

    private handleSignStatusError(error: HttpErrorResponse): Observable<never | undefined> {
        if (this.isTimeOutError(error)) {
            this.qrTimedOut = true;
        } else {
            this.signError.emit({ key: error.error.code, type: 'error' });
        }
        return of(undefined);
    }

    private isTimeOutError(error: HttpErrorResponse): boolean {
        return (error.error && error.error.code === ErrorCode.TASK_EXPIRED) || error.error.code === ErrorCode.NOT_AUTHORIZED;
    }

    private isReady(status: TaskStatus): boolean {
        return status === TaskStatus.AUTHENTICATED || this.isExpired(status) || status !== this.initialStatus();
    }

    private isExpired(status: TaskStatus): boolean {
        return status === TaskStatus.UNAUTHENTICATED_EXPIRED || status === TaskStatus.PARTIALLY_AUTHENTICATED_EXPIRED;
    }
}
